import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';
import { createFollowTracker, followActive } from '@/lib/followActiveNav';
import type { ElLike } from '@/lib/followActiveNav';

/** SPEC-505 — React shell over `lib/followActiveNav`. Keyed on `navKey` and `collapsed`
 *  only, so the user's own scroll or group toggles never trigger it. `activeGroup` is the
 *  page's own verb group (never Favourites) or null for a standalone page. */
interface Options {
  containerRef: RefObject<HTMLElement>;
  navKey: string;
  collapsed: boolean;
  activeGroup: string | null;
  activeGroupOpen: boolean;
  openGroup: (label: string) => void;
}

export function useFollowActiveNav(opts: Options): void {
  const latest = useRef(opts);
  latest.current = opts;
  const tracker = useRef(createFollowTracker());
  const { navKey, collapsed } = opts;
  const wasCollapsed = useRef(collapsed);
  useEffect(() => { if (collapsed) wasCollapsed.current = true; }, [collapsed]);

  useEffect(() => {
    if (!tracker.current.pending(navKey, collapsed)) return;
    const { activeGroup, activeGroupOpen, openGroup } = latest.current;
    const justExpanded = Boolean(activeGroup) && !activeGroupOpen;
    if (activeGroup && justExpanded) openGroup(activeGroup);
    // After layout, so the expanded group and the strip have mounted. A group still
    // animating open (200ms) or a sidebar still widening (300ms) is measured late.
    const delay = justExpanded ? 220 : wasCollapsed.current ? 320 : 0;
    let frame = 0;
    const timer = window.setTimeout(() => {
      frame = requestAnimationFrame(() => {
        const container = latest.current.containerRef.current;
        if (!container) return;
        const reducedMotion = typeof window.matchMedia === 'function'
          && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const result = followActive({
          container: container as unknown as ElLike,
          group: activeGroup,
          justExpanded: false,
          reducedMotion,
        });
        if (result !== 'missing') tracker.current.done(navKey);
        wasCollapsed.current = false;
      });
    }, delay);
    return () => { window.clearTimeout(timer); cancelAnimationFrame(frame); };
  }, [navKey, collapsed]);
}
